"use client";
import React, { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useOpenDaysFilterContext, OpenDaysFilters } from "@packages/lib/utlils/use-context/opendays-filter-context";
import { buildOpenDaysUrl } from "@packages/shared-components/sr-page/open-days-api-utils/opendays-url-utils";
import PlusIconSvg from "./plusIcon-svg";
import ChevronLeftSvg from "./chevronLeftIcon-svg";

const capitalizeFirst = (str: string) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

const formatLocation = (location: string) =>
  location.split("-").map(capitalizeFirst).join(" ");

const formatMonthYear = (monthYear: string) => {
  if (!monthYear) return "";
  const [month, year] = monthYear.split("-");
  return `${capitalizeFirst(month)} ${year}`;
};

const formatEventType = (eventType: string) => {
  return eventType
    .split(",")
    .map((t) => (t.trim() === "in-person" ? "In-person" : "Virtual"))
    .join(", ");
};

const OpenDaysSearchLabels = () => {
  const router = useRouter();
  const { filters, setFilters, setIsLoading } = useOpenDaysFilterContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);

  // Build labels from current filter state
  const labels: { key: keyof OpenDaysFilters; displayName: string; value?: string }[] = [];

  if (filters.studyLevel) {
    labels.push({ key: "studyLevel", displayName: capitalizeFirst(filters.studyLevel) });
  }
  if (filters.provider || filters.providerId) {
    labels.push({ key: "provider", displayName: formatLocation(filters.provider || filters.providerId) });
  }
  if (filters.location) {
    filters.location.split(",").filter(Boolean).forEach(loc => {
      labels.push({ key: "location", displayName: formatLocation(loc), value: loc });
    });
  }
  if (filters.postcode) {
    labels.push({ key: "postcode", displayName: filters.postcode.toUpperCase() });
  }
  if (filters.monthYear) {
    labels.push({ key: "monthYear", displayName: formatMonthYear(filters.monthYear) });
  }
  if (filters.eventType) {
    labels.push({ key: "eventType", displayName: formatEventType(filters.eventType) });
  }
  if (filters.dateFrom && filters.dateTo) {
    const from = new Date(filters.dateFrom).toLocaleDateString("en-GB");
    const to = new Date(filters.dateTo).toLocaleDateString("en-GB");
    labels.push({ key: "dateFrom", displayName: `${from} – ${to}` });
  }

  // Detect overflow after render — only show plus icon when labels genuinely exceed available width
  useEffect(() => {
    const checkOverflow = () => {
      if (!listRef.current) return;
      const el = listRef.current;

      // Check if we're at xl+ breakpoint (1280px) where flex-wrap is active
      const isDesktop = window.matchMedia("(min-width: 1280px)").matches;

      if (isDesktop) {
        // On desktop (xl+): flex-wrap is active with max-h constraining to one row.
        // Temporarily remove max-height to let content wrap naturally, then check
        // if the content height exceeds a single row.
        el.style.setProperty("max-height", "none", "important");
        el.style.setProperty("overflow", "visible", "important");
        void el.offsetHeight; // force reflow

        const firstChild = el.children[0] as HTMLElement | undefined;
        const singleRowHeight = firstChild ? firstChild.offsetHeight : 0;
        // If scrollHeight exceeds one row height (+ tolerance for gap), labels would wrap
        const wouldWrap = singleRowHeight > 0 && el.scrollHeight > singleRowHeight + 12;

        el.style.removeProperty("max-height");
        el.style.removeProperty("overflow");

        setIsOverflowing(wouldWrap);
      } else {
        // On mobile/tablet: horizontal scroll container — check if content overflows
        setIsOverflowing(el.scrollWidth > el.clientWidth + 1);
      }
    };

    // Delay to ensure DOM is painted
    const timer = setTimeout(checkOverflow, 150);
    window.addEventListener("resize", checkOverflow);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [labels.length]);

  const removeFilter = (key: keyof OpenDaysFilters, value?: string) => {
    const updated: OpenDaysFilters = { ...filters };

    if (key === "dateFrom") {
      updated.dateFrom = "";
      updated.dateTo = "";
    } else if (key === "provider") {
      updated.provider = "";
      updated.providerId = "";
    } else if (key === "postcode") {
      updated.postcode = "";
      updated.distance = "any";
    } else if (key === "location" && value) {
      const locations = updated.location.split(",").filter(l => l !== value);
      updated.location = locations.join(",");
    } else {
      (updated as any)[key] = "";
    }

    setFilters(updated);
    setIsLoading(true);
    router.push(buildOpenDaysUrl(updated));
  };

  if (labels.length === 0) return null;

  // Determine if overflow icons should show (only when labels actually overflow the container)
  const showOverflow = isOverflowing;

  return (
    <section className="bg-white px-[16px] py-[8px] md:px-[20px] xl:px-0 z-[3]">
      <div className="max-w-container mx-auto">
        <div className="flex items-start gap-[8px]">
          <ul
            ref={listRef}
            className={`flex items-center gap-[8px] uppercase overflow-x-auto xl:overflow-x-visible xl:flex-wrap ${
              !isExpanded && showOverflow ? "xl:max-h-[28px] xl:overflow-hidden" : ""
            }`}
          >
            {labels.map((item, index) => (
              <li
                key={`${item.key}-${item.value || index}`}
                className="bg-secondary-50 text-blue-500 whitespace-nowrap rounded-[4px] px-[10px] py-[3px] font-semibold x-small flex items-center gap-[2px]"
              >
                {item.displayName}
                <svg
                  onClick={() => removeFilter(item.key, item.value)}
                  className="cursor-pointer ml-[4px]"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 3L3 9M3 3L9 9"
                    stroke="#4664DC"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </li>
            ))}
            {/* Chevron (less-than) icon at end when expanded (xl+ only) */}
            {showOverflow && isExpanded && (
              <li
                className="hidden xl:flex bg-secondary-50 text-blue-500 rounded-[4px] items-center justify-center size-[24px] cursor-pointer shrink-0"
                onClick={() => setIsExpanded(false)}
              >
                <ChevronLeftSvg />
              </li>
            )}
          </ul>
          {/* Plus icon as sibling next to the ul (xl+ only) */}
          {showOverflow && !isExpanded && (
            <div
              className="hidden xl:flex bg-secondary-50 text-blue-500 rounded-[4px] items-center justify-center px-[4px] py-[3px] cursor-pointer shrink-0"
              onClick={() => setIsExpanded(true)}
            >
              <PlusIconSvg />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default OpenDaysSearchLabels;
